import { loadAppStartupData } from 'App/Map/_utils/loadAppStartupData/loadAppStartupData'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useEffect } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { captureFrontendError } from 'utils/captureFrontendError'
import { checkAuthorization } from 'utils/checkAuthorization'
import { usePreparePDF } from './_utils/usePreparePDF'
import { DashboardSection } from './DashboardSection'
import { IcsDashboardControls } from './IcsDashboardControls/IcsDashboardControls'
import { Irrigation } from './Irrigation/Irrigation'
import { IrrigationSkeleton } from './Irrigation/IrrigationSkeleton'
import { NDVI } from './NDVI/NDVI'
import { NDVISkeleton } from './NDVI/NDVISkeleton'
import { ReportHeader } from './ReportHeader'
import { Soil } from './Soil/Soil'
import { SoilSkeleton } from './Soil/SoilSkeleton'
import { useIcsDashboardData } from './useIcsDashboardData'
import { WeeklyIrrigationOverview } from './WeeklyIrrigationOverview/WeeklyIrrigationOverview'
import { WeeklyIrrigationOverviewSkeleton } from './WeeklyIrrigationOverview/WeeklyIrrigationOverviewSkeleton'
import { YTDEtcVsApplied } from './YTDEtcVsApplied/YTDEtcVsApplied'
import { YTDEtcVsAppliedSkeleton } from './YTDEtcVsApplied/YTDEtcVsAppliedSkeleton'

export const IcsDashboard = () => {
  useEffect(() => {
    loadAppStartupData().catch((err) => {
      captureFrontendError(err, { extra: { message: 'Failed to load app startup data' } })
    })
  }, [])

  const selectedPropertyId = selectedFieldAssetsStore.useSelector((s) => s.property)
  const selectedProperty = fieldAssetStore.useSelector((s) => s.properties)?.[Number(selectedPropertyId)]
  const { printDocument } = usePreparePDF()
  const { data: reportData, loadingStates, errorStates, refetchIrrigation, resetKey } = useIcsDashboardData()

  const handleSavePDF = () => {
    if (!selectedProperty?.timezone) return

    const { propertyName, timezone } = selectedProperty
    const isoDate = moment.tz(timezone).format('YYYY-MM-DD')
    const filename = `${propertyName}_${isoDate}`

    printDocument(filename)
  }

  const firstDay = reportData?.nextWeekDates?.dateFrom ?? ''
  const lastDay = reportData?.nextWeekDates?.dateTo ?? ''

  const hasIcsPermission =
    selectedPropertyId !== null &&
    checkAuthorization({
      entity: selectedPropertyId,
      permission: 'VIEW_ICS_DASHBOARD',
    })

  return (
    <div
      css={{
        width: 1200,
        maxWidth: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
        padding: '20px',
      }}
    >
      <IcsDashboardControls
        onSavePDF={handleSavePDF}
        isLoading={loadingStates.irrigation}
        showSavePDF={!!selectedProperty && hasIcsPermission}
      />
      {selectedProperty && hasIcsPermission && (
        <div css={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <ReportHeader dateFrom={firstDay} dateTo={lastDay} />
          <DashboardSection
            error={errorStates.irrigation}
            isLoading={loadingStates.irrigation}
            sectionName={translate.phrases.banyanApp('Irrigation')}
            skeleton={<IrrigationSkeleton />}
          >
            <Irrigation
              eventsResponse={reportData?.eventsResponse ?? null}
              thisWeekDates={reportData?.thisWeekDates ?? { dateFrom: '', dateTo: '' }}
              nextWeekDates={reportData?.nextWeekDates ?? { dateFrom: '', dateTo: '' }}
              dailyEtNextWeek={reportData?.dailyEtNextWeek ?? []}
              dailyEtLastWeek={reportData?.dailyEtLastWeek ?? []}
              refetch={refetchIrrigation}
              resetKey={resetKey}
            />
          </DashboardSection>
          <DashboardSection
            error={errorStates.irrigation}
            hideOnError
            isLoading={loadingStates.irrigation}
            sectionName={translate.phrases.banyanApp('Planned vs Applied')}
            skeleton={<WeeklyIrrigationOverviewSkeleton />}
          >
            <WeeklyIrrigationOverview
              eventsResponse={reportData?.eventsResponse ?? null}
              resetKey={resetKey}
            />
          </DashboardSection>
          <DashboardSection
            error={errorStates.ndvi}
            isLoading={loadingStates.ndvi}
            sectionName={translate.phrases.banyanApp('NDVI')}
            skeleton={<NDVISkeleton />}
          >
            <NDVI data={reportData?.ndvi ?? []} resetKey={resetKey} />
          </DashboardSection>
          <DashboardSection
            error={errorStates.soil}
            isLoading={loadingStates.soil}
            sectionName={translate.phrases.banyanApp('Soil')}
            skeleton={<SoilSkeleton />}
          >
            {reportData?.soil ? <Soil data={reportData.soil} resetKey={resetKey} /> : null}
          </DashboardSection>
          <DashboardSection
            error={errorStates.ytd}
            isLoading={loadingStates.ytd}
            sectionName={translate.phrases.banyanApp('Year to Date ETc vs Applied')}
            skeleton={<YTDEtcVsAppliedSkeleton />}
          >
            <YTDEtcVsApplied data={reportData?.ytd ?? []} resetKey={resetKey} />
          </DashboardSection>
        </div>
      )}
    </div>
  )
}
